import Tooltip, { TooltipTrigger } from '@orn/shared-web/components/Tooltip/Tooltip';
import formatTimeAgo from '@orn/shared-web/utils/formatTimeAgo';
import * as React from 'react';

import Button from '../../../components/Button';
import DangerConfirmDialog from '../../../components/DangerConfirmDialog';
import ReleaseChecklistGate from '../ReleaseChecklistGate';
import {
  derivePublishButtonState,
  formatPostedAtClockTime,
  SHORT_SHA_LENGTH,
  stripChecklistSectionsFromBody,
  type IReleaseChecklistGateState,
} from '../releaseLogic';

import CiStatusBadge from './CiStatusBadge';
import ReleaseBodyMarkdown from './ReleaseBodyMarkdown';

import type { ChecklistSection } from '../../../__generated__/globalTypes';
import type {
  AdminGetCurrentReleaseDraftQuery,
  ReleasePreviewFieldsFragment,
} from '../__generated__/ReleasesScene.gql';
import type { IAnnounceJustPosted } from '../releaseLogic';

interface ICurrentDraftCardProps {
  draft: NonNullable<AdminGetCurrentReleaseDraftQuery['adminGetCurrentReleaseDraft']>;
  /**
   * Re-post handler for the "Re-post Slack" button — bound to
   * `handleAnnounce` upstream because it goes through the same
   * idempotent-retry path (existing draft + same head sha → re-post).
   */
  onRepostSlack: () => void;
  isReposting: boolean;
  /**
   * `view in Slack` permalink shown next to the action header after the
   * operator clicks Re-post. Cleared on the next refetch.
   */
  justPosted: IAnnounceJustPosted | null;
  /**
   * Preview data driving the interactive checklist gate. The parent
   * scene auto-triggers `adminGetReleasePreview` when a draft is open
   * — the LLM cache hits because the draft was just announced, so
   * this typically lands instantly.
   *
   * Null while loading or on error; the gate falls back to a loading
   * / error message, and Publish stays disabled until preview lands.
   */
  preview: ReleasePreviewFieldsFragment | null;
  previewLoading: boolean;
  previewError: boolean;
  /**
   * Set after a publish attempt rejects with `MULTIPLE_DRAFTS_FOUND`.
   * Surfaces a recovery banner and disables Publish until the operator
   * clicks Discard to walk the queue down.
   */
  multipleDraftsDetected: boolean;
  /** Resolves when the publish mutation completes; rejection keeps the dialog open. */
  onPublishConfirmed: () => Promise<void>;
  /** Resolves when the delete mutation completes; rejection keeps the dialog open. */
  onDiscardConfirmed: () => Promise<void>;
  /**
   * Fires when a release manager toggles a checklist box. Parent runs
   * `adminToggleReleaseChecklistItem` and refetches the draft.
   */
  onToggleChecklistItem: (section: ChecklistSection, index: number, checked: boolean, lineContent: string) => void;
}

/**
 * Primary "what's open right now" card. Renders the MOPS draft as the
 * release manager will see it once published — sticky header with tag /
 * sha / drafted-by, the LLM-rendered body as markdown, and live
 * **Publish** / **Discard draft** buttons gated by an interactive
 * checklist over the LLM-generated `prerequisites` and `testPlan`.
 *
 * The Publish flow:
 *   1. Operator must check every box in both checklist sections.
 *   2. CI must be green at the draft's `targetSha` (re-checked
 *      server-side at publish time, but mirrored here for fast
 *      feedback).
 *   3. On click, a typed-confirmation modal asks the operator to type
 *      the tag verbatim.
 *   4. On confirm, the parent calls `adminPublishReleaseDraft`. Server
 *      errors map to operator-facing toast messages via
 *      `mapDraftMutationError`.
 *
 * The Discard flow is one-click + a small confirmation modal (no
 * typing required) — discard is the safe action and the cost of a
 * misclick is "the operator re-announces", not "every user sees a
 * broken release".
 *
 * When this card is rendered, the "Pending release" preview is hidden:
 * the operator already committed to a SHA when they announced, so the
 * preview at HEAD is no longer the actionable state — the draft is.
 */
const CurrentDraftCard = ({
  draft,
  onRepostSlack,
  isReposting,
  justPosted,
  preview,
  previewLoading,
  previewError,
  multipleDraftsDetected,
  onPublishConfirmed,
  onDiscardConfirmed,
  onToggleChecklistItem,
}: ICurrentDraftCardProps) => {
  const [gateState, setGateState] = React.useState<IReleaseChecklistGateState>({
    allChecked: false,
    totalCount: 0,
    checkedCount: 0,
    prereqTotalCount: 0,
    prereqUncheckedCount: 0,
    testPlanTotalCount: 0,
    testPlanUncheckedCount: 0,
  });
  const [isPublishDialogOpen, setIsPublishDialogOpen] = React.useState(false);
  const [isDiscardDialogOpen, setIsDiscardDialogOpen] = React.useState(false);

  const handleGateChange = React.useCallback((next: IReleaseChecklistGateState) => {
    setGateState(next);
  }, []);

  // CI status comes from the preview query (which auto-fires when a
  // draft is open). Treat missing preview as "indeterminate" — Publish
  // stays disabled via the previewLoading / previewError gate, so we
  // don't gate twice on the same condition.
  const ciConclusion = preview?.ciStatus.conclusion ?? 'unknown';

  const publishButtonState = derivePublishButtonState({
    ciConclusion,
    allChecklistItemsChecked: gateState.allChecked,
    prereqUncheckedCount: gateState.prereqUncheckedCount,
    testPlanUncheckedCount: gateState.testPlanUncheckedCount,
    multipleDraftsDetected,
    previewLoading,
    previewError,
  });

  return (
    <section aria-label="Current release draft" className="border-border-primary border">
      {/* Solid `bg-app` matches the body — same elevated-sticky pattern as the preview card. */}
      <div className="bg-app border-border-primary sticky top-0 z-10 border-b px-4 py-3 shadow-lg shadow-black/40">
        <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
          <div className="flex flex-col">
            {/*
              The header colour here is deliberately the warning-amber accent
              (not the default primary white) so it visually separates from a
              *published* release at a glance. The "DRAFT" prefix + amber tag
              value reads as "this isn't done yet — one more click to
              publish". Mirrors the orange `<code>` chip palette so the warm
              tones cluster as one visual language for "operator action
              required" content.
            */}
            <span className="text-fg-warning-primary text-[10px] font-semibold uppercase tracking-widest">
              ⚠ Unpublished draft
            </span>
            <span className="text-fg-warning-primary font-mono text-2xl font-semibold">{draft.tag}</span>
          </div>
          <div className="flex flex-col">
            <span className="text-fg-tertiary text-[10px] uppercase tracking-widest">Target SHA</span>
            <span className="text-fg-secondary font-mono text-sm">{draft.targetSha.slice(0, SHORT_SHA_LENGTH)}</span>
          </div>
          <div className="flex flex-col">
            <span className="text-fg-tertiary text-[10px] uppercase tracking-widest">Drafted by</span>
            <span className="text-fg-secondary font-mono text-sm">
              {draft.draftedByEmail} · {formatTimeAgo(draft.draftedAt)}
            </span>
          </div>
          {preview && (
            <div className="self-center">
              <CiStatusBadge
                conclusion={preview.ciStatus.conclusion}
                successCount={preview.ciStatus.successCount}
                failureCount={preview.ciStatus.failureCount}
                pendingCount={preview.ciStatus.pendingCount}
                totalCount={preview.ciStatus.totalCount}
              />
            </div>
          )}
          <div className="ml-auto flex flex-wrap items-center justify-end gap-3">
            {justPosted && (
              <span className="text-fg-secondary text-xs">
                Posted at {formatPostedAtClockTime(justPosted.postedAt)}
                {justPosted.slackUrl && (
                  <>
                    {' — '}
                    <a
                      href={justPosted.slackUrl}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="text-fg-brand-primary hover:text-fg-brand-primary_alt underline underline-offset-2"
                    >
                      view in Slack
                    </a>
                  </>
                )}
              </span>
            )}
            <Button color="secondary" size="sm" isLoading={isReposting} onClick={() => onRepostSlack()}>
              Re-post Slack
            </Button>
            <Button color="secondary" size="sm" onClick={() => setIsDiscardDialogOpen(true)}>
              Discard draft
            </Button>
            {publishButtonState.isEnabled ? (
              <Button color="danger" size="sm" onClick={() => setIsPublishDialogOpen(true)}>
                Publish {draft.tag}
              </Button>
            ) : (
              <Tooltip
                // `whitespace-pre-line` honours the `\n` join from
                // `derivePublishButtonState` so the multi-line summary
                // ("3 tests require completion\n1 prerequisites require review")
                // renders as actual line breaks.
                title={<span className="whitespace-pre-line">{publishButtonState.tooltip}</span>}
                placement="top"
              >
                <TooltipTrigger>
                  <span className="inline-block">
                    <Button color="danger" size="sm" isDisabled>
                      Publish {draft.tag}
                    </Button>
                  </span>
                </TooltipTrigger>
              </Tooltip>
            )}
            <a
              href={draft.htmlUrl}
              target="_blank"
              rel="noreferrer noopener"
              className="text-fg-brand-primary hover:text-fg-brand-primary_alt text-xs underline underline-offset-2"
            >
              View draft on github.com →
            </a>
          </div>
        </div>
      </div>

      {multipleDraftsDetected && (
        <div className="border-fg-error-primary bg-bg-error-primary/10 border-b px-4 py-3">
          <p className="text-fg-error-primary text-xs leading-relaxed">
            <span className="font-semibold uppercase tracking-widest">⚠ Multiple drafts detected</span>
            <span className="ml-2">
              Two operators raced and created more than one open draft. Click <strong>Discard draft</strong> to remove
              the front-of-queue draft, then refresh until exactly one remains. Publish stays disabled until the queue
              is back to a single draft.
            </span>
          </p>
        </div>
      )}

      <div className="flex flex-col gap-5 px-4 py-4">
        {/*
          Checklist gate — the source of truth for "is the operator
          allowed to publish?". Sourced from the draft body (frozen at
          announce time) rather than the live LLM preview, so the gate
          is available immediately and stays consistent across refreshes
          even after `main` advances and the preview's items drift.
        */}
        <ReleaseChecklistGate
          prerequisiteLineContents={draft.prerequisiteLineContents}
          testPlanLineContents={draft.testPlanLineContents}
          prerequisiteCheckedIndices={draft.prerequisiteCheckedIndices}
          testPlanCheckedIndices={draft.testPlanCheckedIndices}
          prerequisiteCheckedMarkers={draft.prerequisiteCheckedMarkers}
          testPlanCheckedMarkers={draft.testPlanCheckedMarkers}
          onStateChange={handleGateChange}
          onToggle={onToggleChecklistItem}
        />

        {/*
          Strip the static-checkbox `## Prerequisites` and
          `## Pre-release test plan` sections from the body — those
          sections are now surfaced as the interactive checklist gate
          above. Without this strip the operator would see each item
          twice (once in the gate, once in the body markdown).
        */}
        <ReleaseBodyMarkdown body={stripChecklistSectionsFromBody(draft.body)} />
      </div>

      <DangerConfirmDialog
        isOpen={isPublishDialogOpen}
        onOpenChange={setIsPublishDialogOpen}
        confirmValue={draft.tag}
        title={`Publish ${draft.tag}`}
        body={
          <>
            <p>
              This will tag <span className="font-mono">{draft.tag}</span> at{' '}
              <span className="font-mono">{draft.targetSha.slice(0, SHORT_SHA_LENGTH)}</span> and trigger the prod
              deploy via <span className="font-mono">release.yml</span>. Slack gets a rocket reaction on the original
              announce.
            </p>
            <p className="text-fg-secondary mt-2 text-xs">This is irreversible from MOPS — the next stop is prod.</p>
          </>
        }
        confirmLabel={`Publish ${draft.tag}`}
        onConfirm={onPublishConfirmed}
      />

      <DangerConfirmDialog
        isOpen={isDiscardDialogOpen}
        onOpenChange={setIsDiscardDialogOpen}
        title={`Discard ${draft.tag}`}
        body={
          <>
            <p>
              This will delete the GitHub draft <span className="font-mono">{draft.tag}</span> and the original Slack
              announce. The work in <span className="font-mono">{draft.targetSha.slice(0, SHORT_SHA_LENGTH)}</span>{' '}
              stays put — you can re-announce when ready.
            </p>
          </>
        }
        confirmLabel={`Discard ${draft.tag}`}
        onConfirm={onDiscardConfirmed}
      />
    </section>
  );
};

export default CurrentDraftCard;
